// CARDDEFAULT2 (docs/build-briefs/CARDDEFAULT2-pay-defaults-to-saved-card.md, P0.20). Which saved card the Pay sheet opens on.
// Pure: the sheet passes what it already holds and applies the answer.

import type { SavedCardEntryDto } from "./api.ts";

export type DefaultCardScope =
  | { type: "whole" }
  | { type: "guest"; guestId: string }
  | { type: "share" }
  | { type: "amount" }
  | { type: "unassigned" };

/** The one guest whose card the sheet may default to, or undefined when nobody is the single payer. A whole-ticket payment
 * counts only with exactly one guest: with several, whose card it is stays a choice. */
export function payingGuestId(
  scope: DefaultCardScope,
  guests: readonly { id: string }[],
): string | undefined {
  if (scope.type === "guest") return scope.guestId;
  if (scope.type === "whole" && guests.length === 1) return guests[0]?.id;
  return undefined;
}

/** The saved card to open on: the paying guest's own chargeable card, the most recently saved when there are several
 * (provisional: no record of use exists, so saved time stands in). Undefined keeps today's card reader. A card the server
 * would refuse, a held tab and a cash sale never default. */
export function defaultSavedCardId(input: {
  scope: DefaultCardScope | undefined;
  guests: readonly { id: string }[];
  savedCards: readonly SavedCardEntryDto[] | undefined;
  holdActive: boolean;
  cash: boolean;
}): string | undefined {
  if (input.scope === undefined || input.holdActive || input.cash) return undefined;
  const guestId = payingGuestId(input.scope, input.guests);
  if (guestId === undefined) return undefined;
  const usable = (input.savedCards ?? []).filter(
    (entry) => entry.chargeable && entry.guestIds.includes(guestId),
  );
  return newestSavedCardFirst(usable)[0]?.savedCard.id;
}

/** CARDDEFAULT2. The order the lists show cards in: the most recently saved first (saved time stands in for last use). */
export function newestSavedCardFirst<T extends { savedCard: { id: string; created_at: string } }>(
  entries: readonly T[],
): T[] {
  return [...entries].sort((a, b) => {
    const byTime = Date.parse(b.savedCard.created_at) - Date.parse(a.savedCard.created_at);
    return byTime !== 0 ? byTime : a.savedCard.id < b.savedCard.id ? 1 : -1;
  });
}
